import { useState } from "react";
import { toast } from "sonner";
import { Upload, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const TEMPLATE = "name_en,name_ar,title_en,title_ar,email,role\nSara Ahmed,سارة أحمد,Designer,مصممة,sara@inception.sa,employee\n";

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob(["\ufeff" + TEMPLATE], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "employees-template.csv" });
  a.click();
  URL.revokeObjectURL(url);
};

export const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const { t } = useLang();
  const [file, setFile] = useState(null);
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const close = (o) => { if (!o) { setFile(null); setResult(null); } onOpenChange(o); };
  const submit = async () => {
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    form.append("send_invites", invite ? "true" : "false");
    try {
      const { data } = await api.post("/admin/employees/import", form);
      setResult(data);
      if (data.added) { toast.success(t("{count} employees added", { count: data.added })); onDone(); }
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg rounded-3xl" data-testid="import-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">{t("Import employees (CSV)")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{t("Columns: name_en, name_ar, title_en, title_ar, email, role. Only name_en, title_en and email are required. Up to 500 rows.")}</p>
        <button type="button" onClick={downloadTemplate} data-testid="import-template-button"
          className="inline-flex items-center gap-2 self-start text-sm font-semibold text-cobalt hover:underline"><Download className="h-4 w-4" /> {t("Download template")}</button>
        <label className="flex min-h-[52px] cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 text-sm text-slate-600 hover:border-slate-900">
          <Upload className="h-4 w-4" /> <span className="truncate">{file ? file.name : t("Choose CSV file")}</span>
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-file-input" />
        </label>
        <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-slate-800">
          <Checkbox checked={invite} onCheckedChange={(v) => setInvite(!!v)} data-testid="import-invite-checkbox" /> {t("Send invite emails")}
        </label>
        {result && (
          <div className="space-y-1 rounded-2xl bg-slate-50 p-3 text-sm" data-testid="import-result">
            <p className="font-semibold text-slate-900">{t("{count} employees added", { count: result.added })}</p>
            {result.failed.map((f) => (
              <p key={`${f.row}-${f.email}`} className="text-xs text-wade">{t("Row {row}", { row: f.row })} · <span dir="ltr">{f.email}</span> · {t(f.error)}</p>
            ))}
          </div>
        )}
        <button type="button" onClick={submit} disabled={!file || busy} data-testid="import-submit-button"
          className="btn-press min-h-[48px] rounded-2xl bg-slate-900 font-semibold text-white disabled:opacity-60">{busy ? t("Importing…") : t("Import")}</button>
      </DialogContent>
    </Dialog>
  );
};
